import React, { useState, useEffect } from 'react';
import './FeedbackButtons.css';
import SaveAnswerButton from './SaveAnswerButton';

interface FeedbackButtonsProps {
  messageId: string;
  conversationId?: string;
  clientId?: string;
  onFeedback: (messageId: string, type: 'thumbs_up' | 'thumbs_down' | 'need_review', reason?: string) => void;
  disabled?: boolean;
  isWidgetMode?: boolean; // Whether this is in widget mode (public website)
  isAuthenticated?: boolean; // Whether user is authenticated
}

const FEEDBACK_OPTIONS = [
  { value: 'incomplete_answer', label: 'Incomplete answer' },
  { value: 'wrong_answer', label: 'Wrong answer' },
  { value: 'wrong_citation', label: 'Wrong citation/source' },
  { value: 'no_response', label: 'No response' },
  { value: 'response_broken', label: 'Response broken/cut off' },
  { value: 'irrelevant_response', label: 'Irrelevant response' },
  { value: 'hallucination', label: 'Hallucination/made up info' },
  { value: 'poor_context', label: 'Poor context understanding' },
  { value: 'technical_error', label: 'Technical error' }
];

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ 
  messageId,
  conversationId,
  clientId,
  onFeedback, 
  disabled = false,
  isWidgetMode = false,
  isAuthenticated = true
}) => {
  const [showReasonBubble, setShowReasonBubble] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState<string>('');

  // localStorage keys
  const FEEDBACK_STORAGE_KEY = 'chat_feedback_state';

  // Load feedback state from localStorage on mount
  useEffect(() => {
    // Reset state first for each message
    setFeedbackGiven(null);
    setSelectedReason('');
    setShowReasonBubble(false);
    
    const savedFeedback = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    if (savedFeedback) {
      try {
        const feedbackState = JSON.parse(savedFeedback);
        const messageFeedback = feedbackState[messageId];
        
        // Debug logging
        console.log('FeedbackButtons - messageId:', messageId);
        console.log('FeedbackButtons - savedFeedback:', feedbackState);
        console.log('FeedbackButtons - messageFeedback:', messageFeedback);
        
        // Only restore state if this specific message has feedback
        if (messageFeedback && messageFeedback.type) {
          console.log('FeedbackButtons - Restoring feedback:', messageFeedback.type);
          setFeedbackGiven(messageFeedback.type);
          setSelectedReason(messageFeedback.reason || '');
        } else {
          console.log('FeedbackButtons - No feedback found for this message');
        }
      } catch (error) {
        console.error('Error loading feedback state from localStorage:', error);
      }
    } else {
      console.log('FeedbackButtons - No localStorage data found');
    }
  }, [messageId]);

  // Save feedback state to localStorage
  const saveFeedbackToStorage = (type: string, reason?: string) => {
    try {
      const savedFeedback = localStorage.getItem(FEEDBACK_STORAGE_KEY);
      const feedbackState = savedFeedback ? JSON.parse(savedFeedback) : {};
      
      feedbackState[messageId] = {
        type,
        reason: reason || null,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(feedbackState));
    } catch (error) {
      console.error('Error saving feedback state to localStorage:', error);
    }
  };

  const handleThumbsUp = () => {
    if (disabled || feedbackGiven) return;
    setShowReasonBubble(false); // Hide thumbs down tags if shown
    setFeedbackGiven('thumbs_up');
    saveFeedbackToStorage('thumbs_up');
    onFeedback(messageId, 'thumbs_up');
  };

  const handleThumbsDown = () => {
    if (disabled || feedbackGiven) return;
    setShowReasonBubble(true);
  };

  const handleNeedReview = () => {
    if (disabled || feedbackGiven) return;
    setShowReasonBubble(false); // Hide thumbs down tags if shown
    setFeedbackGiven('need_review');
    saveFeedbackToStorage('need_review');
    onFeedback(messageId, 'need_review');
  };

  const handleReasonSelect = (reason: string) => {
    setSelectedReason(reason);
    setFeedbackGiven('thumbs_down');
    setShowReasonBubble(false);
    saveFeedbackToStorage('thumbs_down', reason);
    onFeedback(messageId, 'thumbs_down', reason);
  };


  return (
    <>
      <div className="feedback-buttons">
        <button
          className={`feedback-btn thumbs-up ${feedbackGiven === 'thumbs_up' ? 'active' : ''}`}
          onClick={handleThumbsUp}
          disabled={disabled || !!feedbackGiven}
          title="Helpful response"
          style={{ padding: 0, margin: 0, minWidth: '36px', minHeight: '36px' }}
        >
          👍
        </button>
        
        <button
          className={`feedback-btn thumbs-down ${feedbackGiven === 'thumbs_down' ? 'active' : ''}`}
          onClick={handleThumbsDown}
          disabled={disabled || !!feedbackGiven}
          title="Not helpful"
          style={{ padding: 0, margin: 0, minWidth: '36px', minHeight: '36px' }}
        >
          👎
        </button>
        
        <button
          className={`feedback-btn need-review ${feedbackGiven === 'need_review' ? 'active' : ''}`}
          onClick={handleNeedReview}
          disabled={disabled || !!feedbackGiven}
          title="Needs admin attention"
          style={{ padding: 0, margin: 0, minWidth: '36px', minHeight: '36px' }}
        >
          ⚠️
        </button>

        {/* Save for Training - Only show for authenticated users in app (not widget/public) */}
        {!isWidgetMode && isAuthenticated && (
          <SaveAnswerButton
            messageId={messageId}
            conversationId={conversationId}
            clientId={clientId}
            disabled={disabled || !!feedbackGiven}
          />
        )}

        {feedbackGiven && (
          <span className="feedback-thank-you">
            Thank you for your feedback!
          </span>
        )}
      </div>

      {/* Inline Reason Tags */}
      {showReasonBubble && (
        <div className="feedback-reason-tags">
          <span className="feedback-tags-label">What went wrong?</span>
          <div className="feedback-tags-container">
            {FEEDBACK_OPTIONS.map((option) => (
              <button
                key={option.value}
                className="feedback-tag"
                onClick={() => handleReasonSelect(option.value)}
                style={{ padding: '6px 12px' }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButtons;